import { create } from 'zustand';
import type { BrandingConfig } from '../types/user.types';
import { darken, lighten } from '../utils/formatters';

// WCAG 2.1 relative luminance helpers
function hexLuminance(hex: string): number {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;

  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

// Returns dark text for light backgrounds, white text for dark backgrounds.
// Threshold 0.179 is the luminance at which white drops below 4.5:1 WCAG AA.
function pickForeground(bgHex: string): string {
  return hexLuminance(bgHex) > 0.179 ? '#1a1a1a' : '#ffffff';
}

interface BrandingState {
  config: BrandingConfig | null;
  isLoaded: boolean;

  setBranding: (config: BrandingConfig) => void;
  applyTheme: () => void;
  reset: () => void;
}

export const useBrandingStore = create<BrandingState>()((set, get) => ({
  config: null,
  isLoaded: false,

  setBranding: (config) => {
    set({ config, isLoaded: true });
    get().applyTheme();
  },

  reset: () => set({ config: null, isLoaded: false }),

  applyTheme: () => {
    const { config } = get();
    if (!config) return;

    const root = document.documentElement;
    const primary = config.primary_color;
    const secondary = config.secondary_color;

    root.style.setProperty('--color-primary', primary);
    root.style.setProperty('--color-primary-dark', darken(primary, 10));
    root.style.setProperty('--color-primary-light', lighten(primary, 15));

    // Primary foreground: dark text on light primary, white text on dark primary
    root.style.setProperty('--color-primary-foreground', pickForeground(primary));

    // Sidebar bg: darken primary by 5%. If still too light (luminance > 0.12),
    // clamp to a safe dark slate to keep the sidebar always readable.
    const rawSidebarBg = darken(primary, 5);
    const sidebarBg = hexLuminance(rawSidebarBg) > 0.12 ? '#1f2937' : rawSidebarBg;
    root.style.setProperty('--color-sidebar-bg', sidebarBg);
    root.style.setProperty('--color-sidebar-dark', darken(primary, 15));
    root.style.setProperty('--color-sidebar-item', lighten(primary, 5));

    // Sidebar text color derived from the (possibly clamped) sidebar bg
    const sidebarTextColor = pickForeground(sidebarBg);
    root.style.setProperty('--sidebar-text-color', sidebarTextColor);

    // Hover overlay: translucent dark tint on light sidebars, white tint on dark
    const sidebarOverlay =
      sidebarTextColor === '#1a1a1a'
        ? 'rgba(0,0,0,0.08)'
        : 'rgba(255,255,255,0.10)';
    root.style.setProperty('--sidebar-overlay-color', sidebarOverlay);

    if (secondary) {
      root.style.setProperty('--color-secondary', secondary);
      root.style.setProperty('--color-secondary-foreground', pickForeground(secondary));
    }

    // Tab title
    document.title = config.tab_title || 'Fast Policy Enterprise';

    // Favicon
    if (config.favicon_url) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = config.favicon_url;
    }
  },
}));
